import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight, MapPin } from "lucide-react";
import Seo, { SITE, localBusinessLd, breadcrumbLd } from "@/lib/seo";
import Breadcrumbs from "@/components/Breadcrumbs";
import { GUIDES, guideUrl } from "@/constants/guides";
import { ZONES, VILLES, TERRITOIRES, zoneUrl } from "@/constants/zones";

const fmt = (d) => new Date(d).toLocaleDateString("fr-FR", { year: "numeric", month: "long" });

const TABS = [
  { key: "guides", to: "/blog", label: "Guides & conseils" },
  { key: "zones", to: "/blog/zones-intervention", label: "Zones d'intervention" },
];

const META = {
  guides: {
    title: "Blog — Guides & conseils de rénovation intérieure | RAMA DÉCO",
    description:
      "Méthodes, étapes techniques et durées réelles : nos guides de rénovation intérieure écrits par des professionnels du second œuvre en Île-de-France.",
    path: "/blog",
    h1: ["Comprendre vos travaux", "avant de vous lancer."],
    chapo:
      "L'ordre technique des opérations, les erreurs qui coûtent cher, les durées réelles d'un chantier. Ces guides sont écrits à partir de nos propres chantiers en Île-de-France, sans langue de bois commerciale.",
  },
  zones: {
    title: "Blog — Nos zones d'intervention en Île-de-France | RAMA DÉCO",
    description:
      "RAMA DÉCO intervient à Villepinte, Paris et dans les huit départements d'Île-de-France. Trouvez votre ville et obtenez un devis gratuit sans majoration de zone.",
    path: "/blog/zones-intervention",
    h1: ["Une entreprise de rénovation", "près de chez vous."],
    chapo:
      "Basés à Villepinte, nous intervenons dans toute l'Île-de-France : Paris, Seine-Saint-Denis, Val-d'Oise, Seine-et-Marne, Hauts-de-Seine, Val-de-Marne, Yvelines et Essonne. Déplacement et devis gratuits, sans majoration de zone.",
  },
};

function TabBar({ active }) {
  return (
    <div data-testid="blog-tabs" className="mt-10 flex flex-wrap gap-2 border-b border-black/10 pb-px">
      {TABS.map((t) => (
        <Link
          key={t.key}
          to={t.to}
          data-testid={`blog-tab-${t.key}`}
          className={`px-5 py-3 text-[0.72rem] tracking-[0.14em] uppercase font-semibold transition-colors border-b-2 -mb-px ${
            active === t.key
              ? "border-bordeaux-700 text-bordeaux-700"
              : "border-transparent text-ink-soft hover:text-anthracite"
          }`}
        >
          {t.label}
        </Link>
      ))}
    </div>
  );
}

function GuidesTab() {
  const [lead, ...rest] = GUIDES;
  return (
    <>
      <Link
        to={guideUrl(lead.slug)}
        data-testid={`guide-card-${lead.slug}`}
        className="mt-12 grid lg:grid-cols-2 overflow-hidden rounded-rama-md border border-black/10 bg-white group"
      >
        <div className="aspect-[16/10] lg:aspect-auto lg:min-h-[340px] overflow-hidden">
          <img src={lead.img} alt={lead.h1} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
        </div>
        <div className="p-8 md:p-12 flex flex-col justify-center">
          <div className="font-mono-caption text-bordeaux-700 mb-4">{lead.kicker} · {fmt(lead.date)} · {lead.readTime}</div>
          <h2 className="font-display text-2xl md:text-4xl text-anthracite leading-tight">{lead.h1}</h2>
          <p className="mt-4 text-ink-soft leading-relaxed">{lead.chapo}</p>
          <span className="mt-6 inline-flex items-center gap-2 text-anthracite font-semibold link-underline">Lire le guide <ArrowRight size={16} /></span>
        </div>
      </Link>

      <div className="mt-6 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {rest.map((g) => (
          <Link
            key={g.slug}
            to={guideUrl(g.slug)}
            data-testid={`guide-card-${g.slug}`}
            className="group flex flex-col overflow-hidden rounded-rama-md border border-black/10 bg-white hover:border-bordeaux-700 transition-colors"
          >
            <div className="aspect-[16/10] overflow-hidden">
              <img src={g.img} alt={g.h1} loading="lazy" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
            </div>
            <div className="p-6 flex flex-col flex-1">
              <div className="font-mono-caption text-bordeaux-700 mb-3">{g.kicker} · {g.readTime}</div>
              <h2 className="font-display text-xl text-anthracite leading-tight">{g.h1}</h2>
              <p className="mt-3 text-sm text-ink-soft leading-relaxed flex-1">{g.chapo.slice(0, 130)}…</p>
              <span className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-anthracite">Lire <ArrowUpRight size={14} /></span>
            </div>
          </Link>
        ))}
      </div>
    </>
  );
}

function ZonesTab() {
  const groups = [];
  VILLES.forEach((v) => {
    const g = groups.find((x) => x.dept === v.dept);
    if (g) g.villes.push(v);
    else groups.push({ dept: v.dept, villes: [v] });
  });

  return (
    <>
      <h2 className="mt-14 font-display text-2xl md:text-4xl tracking-tight text-anthracite">Départements & région</h2>
      <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {TERRITOIRES.map((z) => (
          <Link
            key={z.slug}
            to={zoneUrl(z.slug)}
            data-testid={`zones-territoire-${z.slug}`}
            className="group relative overflow-hidden rounded-rama-md border border-black/10 bg-anthracite text-ivory p-7 min-h-[170px] flex flex-col justify-end transition-transform duration-300 hover:-translate-y-1"
          >
            <img src={z.img} alt={`Rénovation ${z.name}`} loading="lazy" className="absolute inset-0 w-full h-full object-cover opacity-30 group-hover:opacity-40 transition-opacity duration-500" />
            <div className="absolute inset-0 bg-gradient-to-t from-anthracite via-anthracite/70 to-anthracite/20" />
            <div className="relative">
              <div className="font-mono-caption text-greige mb-2">{z.cp}</div>
              <div className="font-display text-xl leading-tight">{z.name}</div>
              <div className="mt-2 text-xs text-white/70 inline-flex items-center gap-1.5">Voir la page <ArrowUpRight size={13} /></div>
            </div>
          </Link>
        ))}
      </div>

      <h2 className="mt-16 font-display text-2xl md:text-4xl tracking-tight text-anthracite">Nos villes d'intervention</h2>
      <p className="mt-3 text-ink-soft max-w-2xl">
        Chaque page détaille le bâti local, les travaux les plus demandés et le délai de visite.
      </p>

      <div className="mt-10 space-y-12">
        {groups.map((g) => (
          <div key={g.dept} data-testid={`zones-group-${g.dept}`}>
            <div className="font-mono-caption text-bordeaux-700 mb-4">{g.dept}</div>
            <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px bg-black/10 border border-black/10 rounded-rama-md overflow-hidden">
              {g.villes.map((z) => (
                <li key={z.slug}>
                  <Link
                    to={zoneUrl(z.slug)}
                    data-testid={`zones-ville-${z.slug}`}
                    className="flex items-center justify-between gap-4 bg-white hover:bg-ivory p-6 h-full transition-colors group"
                  >
                    <span>
                      <span className="block font-display text-xl text-anthracite group-hover:text-bordeaux-700 transition-colors">{z.name}</span>
                      <span className="block text-xs text-ink-soft mt-1">{z.cp}</span>
                    </span>
                    <MapPin size={16} className="text-bordeaux-700 shrink-0" />
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>

      <div className="mt-16 p-8 md:p-12 bg-greige/40 rounded-rama-md">
        <h2 className="font-display text-2xl md:text-3xl text-anthracite">Votre commune n'est pas dans la liste ?</h2>
        <p className="mt-3 text-ink-soft max-w-2xl">
          Nous intervenons dans les huit départements d'Île-de-France. Décrivez votre chantier : vous obtenez une
          première évaluation immédiate et nous confirmons la date de visite.
        </p>
        <div className="mt-6 flex flex-col sm:flex-row gap-3">
          <Link to="/estimation" className="btn-primary" data-testid="zones-cta-estimation-bas">Décrire mon projet <ArrowRight size={16} /></Link>
          <Link to="/contact" className="btn-ghost">Nous contacter</Link>
        </div>
      </div>
    </>
  );
}

export default function BlogPage({ tab = "guides" }) {
  const m = META[tab];
  const crumbs =
    tab === "guides"
      ? [{ to: "/", label: "Accueil" }, { to: "/blog", label: "Blog" }]
      : [{ to: "/", label: "Accueil" }, { to: "/blog", label: "Blog" }, { to: "/blog/zones-intervention", label: "Zones d'intervention" }];

  const jsonLd = [localBusinessLd(tab === "zones" ? ZONES.map((z) => z.name) : []), breadcrumbLd(crumbs)];
  if (tab === "guides") {
    jsonLd.push({
      "@context": "https://schema.org",
      "@type": "Blog",
      name: "Blog RAMA DÉCO",
      description: "Guides et conseils de rénovation intérieure.",
      inLanguage: "fr-FR",
      blogPost: GUIDES.map((g) => ({
        "@type": "BlogPosting",
        headline: g.h1,
        datePublished: g.date,
        url: `${SITE.url}${guideUrl(g.slug)}`,
        author: { "@type": "Organization", name: SITE.name },
      })),
    });
  } else {
    jsonLd.push({
      "@context": "https://schema.org",
      "@type": "ItemList",
      name: "Zones d'intervention RAMA DÉCO",
      itemListElement: ZONES.map((z, i) => ({
        "@type": "ListItem",
        position: i + 1,
        name: `Rénovation ${z.name}`,
        url: `${SITE.url}${zoneUrl(z.slug)}`,
      })),
    });
  }

  return (
    <div data-testid={`blog-page-${tab}`} className="pt-[100px] md:pt-[140px] pb-24">
      <Seo title={m.title} description={m.description} path={m.path} jsonLd={jsonLd} />
      <div className="wrap">
        <Breadcrumbs items={crumbs} />
        <div className="font-mono-caption text-bordeaux-700 mt-8 mb-4">Blog RAMA DÉCO</div>
        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-tight text-anthracite max-w-4xl leading-[1.02]">
          {m.h1[0]} <span className="italic text-bordeaux-700">{m.h1[1]}</span>
        </h1>
        <p className="mt-6 max-w-2xl text-ink-soft text-base md:text-lg leading-relaxed">{m.chapo}</p>

        <TabBar active={tab} />

        {tab === "guides" ? <GuidesTab /> : <ZonesTab />}

        <div className="mt-16 p-8 md:p-12 bg-anthracite text-ivory rounded-rama-md grid md:grid-cols-12 gap-8 items-center">
          <div className="md:col-span-8">
            <h2 className="font-display text-2xl md:text-3xl">Une évaluation de votre projet en 5 minutes.</h2>
            <p className="mt-3 text-white/70 max-w-xl">
              Décrivez votre logement et vos travaux avec vos propres mots. Le devis ferme, lui, est établi après la
              visite technique — gratuite et sans engagement.
            </p>
          </div>
          <div className="md:col-span-4 flex flex-col gap-3">
            <Link to="/estimation" data-testid="blog-cta-estimation" className="btn-primary justify-center">Estimer mon projet <ArrowRight size={16} /></Link>
            <a href={`tel:${SITE.phone}`} className="btn-ghost btn-ghost-light justify-center">Appeler {SITE.phoneDisplay}</a>
          </div>
        </div>
      </div>
    </div>
  );
}
